import { reuseTypes } from '../services/parameters';
import { fertilizerTypes } from '../services/fertilizer';
import { getIonValues } from '../services/parameters';
import { outputColumns as outputColumnFields } from '../services/model';
import { loadingStates } from './constants';

const fillWithTestValues = false;

/**
 * @typedef {import('../services/parameters').TCropConfigRaw} TCropConfigRaw
 * @typedef {import('../services/parameters').TIonValues} TIonValues
 * @typedef {import('./readSettings').TConfig} TConfig
 * @typedef {import('./readSettings').TCropConfig} TCropConfig
 * @typedef {import('./readSettings').TSubstrateConfig} TSubstrateConfig
 *
 * @typedef {Object} TOutputColumnConfig
 * @property {string} dataKey
 * @property {string} label
 * @property {boolean} visible
 *
 * @typedef {Object} TStreefwaarden
 * @property {string} substrateCode - vs=veensubstraat, kk=kokos
 * @property {TIonValues} values
 *
 * @typedef {Object} TFertilizerSettings
 * @property {string} fertilizerType - Liquid, Solid or Liquid_Calcium
 * @property {number} tankSize - For solid only, tank size in m3
 * @property {number} concentrateFactor - For solid only
 * @property {boolean} abTanks - For future use
 * @property {string[]} priority
 *
 * @typedef {Object} TCropConfig
 *
 * @typedef {Object} TStore
 * @property {string} key
 * @property {boolean} storeKeyInBrowser
 * @property {number} loadingState - constants.loadingStates
 * @property {Object|string} loadingError - React node
 * @property {string} modelVersion
 * @property {TConfig} config
 * @property {Object} input
 * @property {TCropConfig} input.selectedCropParameter
 * @property {Object} input.reuseDrainwaterValue
 * @property {boolean} input.reuseDrainwaterValue.reuse
 * @property {string} input.reuseDrainwaterValue.reuseType
 * @property {string} input.reuseDrainwaterValue.fixedPercentage string containing number
 * @property {string} input.reuseDrainwaterValue.EC string containing number
 * @property {string} input.dropWaterValue string containing number
 * @property {TIonValues} input.ionValues input/table values
 * @property {string} input.selectedStage
 * @property {TSubstrateConfig} input.selectedSubstrate
 * @property {boolean} input.chlorineValue
 * @property {*} input.teeltfase
 * @property {*} input.ureumValue
 * @property {*} input.summerWinter
 * @property {boolean} input.useDrainAnalysis For potplant model: show drainAnalysis column for input instead of 1:1.5
 * @property {boolean} input.useDefaultValues
 * @property {TFertilizerSettings} input.fertilizerSettings
 * @property {*} modelOutput
 * @property {*} modelOutputElecCorrect
 * @property {*} modelOutputFertilizer
 * @property {*} modelOutputFertilizerBalanced
 * @property {*} fieldErrors
 * @property {Object} outputColumns
 * @property {TOutputColumnConfig} outputColumns.advies
 * @property {TOutputColumnConfig} outputColumns.svo
 * @property {TOutputColumnConfig} outputColumns.verschil
 * @property {boolean} showElecCorrect
 *
 *
 * @param {*} modelVersion
 * @returns {TStore}
 */
const initialState = (modelVersion) => ({
    modelVersion,
    key: null,
    storeKeyInBrowser: false,
    loadingState: loadingStates.initializing,
    loadingError: null,
    config: null,
    input: {
        selectedCropParameter: '', // Object crop, cropStages, ...
        reuseDrainwaterValue: {
            reuse: false,
            reuseType: reuseTypes.fixed,
            fixedPercentage: '20',
            EC: '0.5',
        },
        dropWaterValue: '',
        ionValues: getIonValues(fillWithTestValues, modelVersion),
        // substrate:
        selectedStage: '',
        selectedSubstrate: '',
        chlorineValue: false,
        // potplant:
        teeltfase: '',
        ureumValue: {
            used: false,
            percentage: '10',
            drain: '',
        },
        summerWinter: '',
        useDrainAnalysis: false,
        useDefaultValues: false,
        //
        fertilizerSettings: {
            fertilizerType: fertilizerTypes.liquid,
            tankSize: 1,
            concentrateFactor: 100,
            priority: [
                'Cl',
                'Ca',
                'NH4',
                'P',
                'S',
                'Mg',
                'NO3',
                'Si',
                'K',
                'Fe',
                'Mn',
                'Zn',
                'B',
                'Cu',
                'Mo',
            ],
            abTanks: true,
        },
    },
    modelOutput: null,
    modelOutputElecCorrect: null,
    modelOutputFertilizer: null,
    modelOutputFertilizerBalanced: null,
    fieldErrors: null,
    outputColumns: {
        advies: {
            dataKey: outputColumnFields.advies,
            label: 'Recept',
            visible: true,
        },
        svo: {
            dataKey: outputColumnFields.svo,
            label: 'SVO',
            visible: false,
        },
        verschil: {
            dataKey: outputColumnFields.verschil,
            label: 'Verschil',
            visible: false,
        },
    },
    showElecCorrect: false,
});

/**
 * config: {
 *      cropList: {
 *              crop: string,
 *              cropStages: string[], // phases
 *              drainReuse: boolean, //drain_hergebruik
 *              substrates: string[], //substraat
 *          }[]
 * }
 */

export default initialState;
